import React from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { CheckCircle2, Download, RefreshCcw, Clock, User, Package, CreditCard, FileText, PenTool, Shield } from 'lucide-react';

export const SuccessScreen = ({ formData, onReset, onDownloadPdf, t, language, contractId, submittedAt, apiUrl }) => {
  const success = t.success;
  const locale = language === 'de' ? 'de-DE' : 'en-US';

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: 'EUR'
    }).format(amount);
  };

  const formatTimestamp = (isoString) => {
    if (!isoString) return '-';
    const date = new Date(isoString);
    return new Intl.DateTimeFormat(locale, {
      dateStyle: 'long',
      timeStyle: 'medium',
    }).format(date);
  };

  // Persistent PDF download via backend
  const handlePersistentDownload = () => {
    if (contractId && apiUrl) {
      window.open(`${apiUrl}/api/contract/${contractId}/pdf`, '_blank');
    } else {
      // Fallback to client-side PDF generation
      onDownloadPdf();
    }
  };

  return (
    <div className="max-w-2xl mx-auto animate-fade-in space-y-6">
      {/* Success Header */}
      <Card className="border-0 shadow-xl bg-gradient-card">
        <CardContent className="pt-8 pb-6 px-8">
          <div className="text-center space-y-4">
            {/* Success Icon */}
            <div className="relative mx-auto w-20 h-20">
              <div className="absolute inset-0 rounded-full bg-success/20 animate-ping" />
              <div className="relative w-20 h-20 rounded-full bg-success flex items-center justify-center">
                <CheckCircle2 className="w-10 h-10 text-success-foreground" />
              </div>
            </div>

            <div>
              <h2 className="text-2xl font-semibold text-foreground mb-2">
                {success.title}
              </h2>
              <p className="text-muted-foreground">
                {success.message}
              </p>
            </div>

            {/* Timestamp */}
            {submittedAt && (
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-muted/50 border border-border text-sm text-muted-foreground">
                <Clock className="w-4 h-4" />
                <span>{language === 'de' ? 'Unterzeichnet am' : 'Signed on'}: <strong className="text-foreground">{formatTimestamp(submittedAt)}</strong></span>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Contract Receipt */}
      <Card className="border-0 shadow-lg bg-gradient-card">
        <CardContent className="pt-6 pb-6 px-6">
          <div className="flex items-center gap-2 mb-4">
            <FileText className="w-5 h-5 text-primary" />
            <h3 className="font-semibold text-lg text-foreground">
              {language === 'de' ? 'Vertragsquittung' : 'Contract Receipt'}
            </h3>
          </div>

          <div className="space-y-4">
            {/* Customer Number */}
            {formData.customerInfo.customerNumber && (
              <div className="p-3 rounded-lg bg-green-50 border border-green-200">
                <div className="flex items-center gap-2">
                  <Shield className="w-4 h-4 text-green-600" />
                  <span className="text-sm text-green-700 font-medium">
                    {language === 'de' ? 'Kundennummer' : 'Customer Number'}: {formData.customerInfo.customerNumber}
                  </span>
                </div>
              </div>
            )}

            {/* Customer Info */}
            <div className="p-4 rounded-lg bg-muted/30 border border-border">
              <div className="flex items-center gap-2 mb-3">
                <User className="w-4 h-4 text-primary" />
                <span className="font-medium text-sm text-foreground">
                  {language === 'de' ? 'Kundeninformationen' : 'Customer Information'}
                </span>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm">
                <div>
                  <span className="text-muted-foreground">{language === 'de' ? 'Name' : 'Name'}:</span>
                  <span className="ml-2 font-medium">{formData.customerInfo.name}</span>
                </div>
                {formData.customerInfo.companyName && (
                  <div>
                    <span className="text-muted-foreground">{language === 'de' ? 'Firma' : 'Company'}:</span>
                    <span className="ml-2 font-medium">{formData.customerInfo.companyName}</span>
                  </div>
                )}
                <div>
                  <span className="text-muted-foreground">{language === 'de' ? 'E-Mail' : 'Email'}:</span>
                  <span className="ml-2 font-medium">{formData.customerInfo.email}</span>
                </div>
                <div>
                  <span className="text-muted-foreground">{language === 'de' ? 'Telefon' : 'Phone'}:</span>
                  <span className="ml-2 font-medium">{formData.customerInfo.phone}</span>
                </div>
                <div className="sm:col-span-2">
                  <span className="text-muted-foreground">{language === 'de' ? 'Adresse' : 'Address'}:</span>
                  <span className="ml-2 font-medium">{formData.customerInfo.address}</span>
                </div>
              </div>
            </div>

            {/* Product Details */}
            <div className="p-4 rounded-lg bg-muted/30 border border-border">
              <div className="flex items-center gap-2 mb-3">
                <Package className="w-4 h-4 text-primary" />
                <span className="font-medium text-sm text-foreground">
                  {language === 'de' ? 'Produktdetails' : 'Product Details'}
                </span>
              </div>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">RenewBee PowerPack x{formData.productDetails.quantity}</span>
                  <span className="font-medium">{formatCurrency(formData.productDetails.subtotal || formData.productDetails.netPrice)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">{language === 'de' ? 'MwSt. (19%)' : 'VAT (19%)'}</span>
                  <span className="font-medium">{formatCurrency(formData.productDetails.vat || formData.productDetails.vatAmount)}</span>
                </div>
                <div className="flex justify-between pt-2 border-t border-border">
                  <span className="font-semibold text-foreground">{language === 'de' ? 'Gesamtbetrag' : 'Total'}</span>
                  <span className="font-bold text-primary text-base">{formatCurrency(formData.productDetails.totalPrice)}</span>
                </div>
              </div>
            </div>

            {/* Payment Method */}
            <div className="p-4 rounded-lg bg-muted/30 border border-border">
              <div className="flex items-center gap-2 mb-2">
                <CreditCard className="w-4 h-4 text-primary" />
                <span className="font-medium text-sm text-foreground">
                  {language === 'de' ? 'Zahlungsart' : 'Payment Method'}
                </span>
              </div>
              <p className="text-sm text-muted-foreground">
                {formData.usePaymentPlan 
                  ? (language === 'de' ? 'Zahlungsplan (50% / 25% / 25%)' : 'Payment Plan (50% / 25% / 25%)')
                  : (language === 'de' ? 'Vollzahlung bei Vertragsunterzeichnung' : 'Full payment at contract signing')
                }
              </p>
            </div>

            {/* Signature Info */}
            <div className="p-4 rounded-lg bg-muted/30 border border-border">
              <div className="flex items-center gap-2 mb-3">
                <PenTool className="w-4 h-4 text-primary" />
                <span className="font-medium text-sm text-foreground">
                  {language === 'de' ? 'Unterschrift' : 'Signature'}
                </span>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm">
                <div>
                  <span className="text-muted-foreground">{language === 'de' ? 'Unterzeichnet von' : 'Signed by'}:</span>
                  <span className="ml-2 font-medium">{formData.signature.name}</span>
                </div>
                <div>
                  <span className="text-muted-foreground">{language === 'de' ? 'Ort' : 'Location'}:</span>
                  <span className="ml-2 font-medium">{formData.signature.location}</span>
                </div>
                <div>
                  <span className="text-muted-foreground">{language === 'de' ? 'Datum' : 'Date'}:</span>
                  <span className="ml-2 font-medium">
                    {formData.signature.date instanceof Date
                      ? formData.signature.date.toLocaleDateString(locale)
                      : formData.signature.date
                    }
                  </span>
                </div>
                {submittedAt && (
                  <div>
                    <span className="text-muted-foreground">{language === 'de' ? 'Zeitstempel' : 'Timestamp'}:</span>
                    <span className="ml-2 font-medium text-xs">{formatTimestamp(submittedAt)}</span>
                  </div>
                )}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Download & Actions */}
      <Card className="border-0 shadow-lg bg-gradient-card">
        <CardContent className="pt-6 pb-6 px-6">
          <div className="text-center space-y-4">
            <div className="p-4 rounded-lg bg-amber-50 border border-amber-200">
              <p className="text-sm text-amber-800">
                <strong>{language === 'de' ? 'Wichtig:' : 'Important:'}</strong>{' '}
                {language === 'de' 
                  ? 'Bitte laden Sie Ihren unterschriebenen Vertrag als PDF herunter. Dies dient als Ihr Nachweis der Vertragsunterzeichnung.'
                  : 'Please download your signed contract as PDF. This serves as your proof of contract signing.'
                }
              </p>
            </div>

            <div className="flex flex-col sm:flex-row gap-3">
              <Button 
                onClick={handlePersistentDownload}
                className="flex-1"
                variant="premium"
                size="lg"
              >
                <Download className="w-5 h-5 mr-2" />
                {success.downloadPdf}
              </Button>
              <Button 
                onClick={onReset}
                variant="outline"
                size="lg"
                className="flex-1"
              >
                <RefreshCcw className="w-5 h-5 mr-2" />
                {success.newContract}
              </Button>
            </div>

            {contractId && (
              <p className="text-xs text-muted-foreground">
                {language === 'de' ? 'Vertrags-ID' : 'Contract ID'}: {contractId}
              </p>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default SuccessScreen;
